import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BsChevronDown,
  BsSearch,
  BsDownload,
  BsMusicNoteBeamed,
  BsLink,
  BsWhatsapp,
} from 'react-icons/bs';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import ClipboardJS from 'clipboard';
import 'react-h5-audio-player/lib/styles.css';
import Moment from 'moment';

import Index from '~/pages/Index';
import Modal from '~/components/Modal';
import { lower } from '~/utils/lower';
import Posts from '~/components/Posts';
import Highlight from '~/components/Highlight';
import { ReactComponent as UndrawNoData } from '~/assets/img/undraw_no_data.svg';


import { useProfile } from '~/hooks/profile';
import { useToast } from '~/hooks/toast';
import Loading from '~/components/Loading';
import Pagination from '~/components/Pagination';
import { getStatusByCode } from '../../utils/statusCertificateEnum';
import { useFindUserMusicsQuery } from '../../graphql/generated/graphql';
import * as S from './styles';

const isCertified = statusId => statusId === 21 || statusId === 22;

const getNames = people =>
  Array.isArray(people)
    ? people.map(person => person?.name).filter(Boolean)
    : [];

const creditLabel = (names, singular, plural) =>
  names.length > 1 ? plural : singular;

const MinhasMusicas = () => {
  const { dataProfile, profileComplete } = useProfile();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [word, setWord] = useState('');
  const [modalShow, setModalShow] = useState('');
  const [selectedMusic, setSelectedMusic] = useState(null);
  // const [musics, setMusics] = useState(null);
  const [downloadMP3, setDownloadMP3] = useState();
  const [downloadingAudio, setDownloadingAudio] = useState(false);
  const [musicRegister, setMusicRegister] = useState();
  const [MP3, setMP3] = useState();
  const [extension, setExtension] = useState();

  // const [inteiro, setInteiro] = useState();
  const [currentPages, setCurrentPages] = useState(1);
  const [totalEstimado, setTotalEstimado] = useState(0);
  const limit = 10;

  const { data: dataMusics, loading: loadingMusics } = useFindUserMusicsQuery({
    variables: {
      data: {
        limit,
        page: currentPages,
        title: word,
        date: ""
      },
    },
  });

      // Calcular total estimado de forma estável
  useEffect(() => {
    if (dataMusics?.findUserMusics) {
      const currentResults = dataMusics.findUserMusics.length;
      const isLastPage = currentResults < limit;

      if (isLastPage) {
        // É a última página, calcular total exato
        const exactTotal = (currentPages - 1) * limit + currentResults;
        setTotalEstimado(exactTotal);
      } else {
        // Não é a última página, estimar que há pelo menos mais uma página
        const minTotal = currentPages * limit + 1;
        setTotalEstimado(prev => Math.max(prev, minTotal));
      }
    } else if (!dataMusics && !loadingMusics) {
      // Se não há dados e não está carregando, resetar para 0
      setTotalEstimado(0);
    }
  }, [dataMusics, currentPages, limit, loadingMusics]);


    const handleChange = useCallback(e => {
    const newWord = e.target.value;
    setWord(newWord);

    // Resetar para primeira página e total ao pesquisar
    if (currentPages !== 1) {
      setCurrentPages(1);
    }
    setTotalEstimado(0); // Resetar total para recalcular com nova pesquisa
  }, [currentPages]);

  const handleClickModalMusic = async music => {
    setSelectedMusic(music);

    if(music.files) {
      setExtension(music.files.extension);
    } else {
      setExtension(null);
    }

    const urlMusicRegister = await `${process.env.REACT_APP_URL}/musica-registrada/${music.key}`;
    setMusicRegister(urlMusicRegister);

    if (music.linkAudio) {
      // const urlStream = await `${process.env.REACT_APP_URL_API}/api/v1/guest/music/stream/${music.key}`;
      // const urlDownloadMP3 = await `${process.env.REACT_APP_URL_API}/api/v1/guest/music/stream/${music.key}`;

      setMP3(music.linkAudio);
      setDownloadMP3(music.linkAudio);
    } else {
      setMP3(null);
      setDownloadMP3(null);
    }

  }

  const handleRegisterMusic = () => {
    if (dataProfile.wallet.creditUsed > 0 && profileComplete) {
      navigate('/dashboard/registrar-musica');
    } else if (!profileComplete) {
      return setModalShow('showNotProfile');
    } else if (dataProfile.wallet.creditUsed <= 0) {
      return setModalShow('showNotCredit');
    }
  };

  const downloadMusic = async () => {
    if (!downloadMP3 || downloadingAudio) return;

    setDownloadingAudio(true);

    try {
      const response = await fetch(downloadMP3);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blobUrl = URL.createObjectURL(await response.blob());
      const fileName = selectedMusic.title.replace(/[\\/:*?"<>|]/g, '').trim() || 'musica';
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${fileName}.${extension || 'mp3'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      window.open(downloadMP3, '_blank');
    } finally {
      setDownloadingAudio(false);
    }
  };

  const copyMusicLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(musicRegister);
      } else {
        ClipboardJS.copy(musicRegister);
      }

      addToast({
        type: 'success',
        title: 'Link copiado',
        description: 'Agora é só colar e enviar para quem você quiser.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Não foi possível copiar o link. Tente novamente.',
      });
    }
  };

  const shareMusicLink = () => {
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(musicRegister)}`,
      '_blank'
    );
  };

  const openCertificate = (event, music) => {
    event.stopPropagation();
    window.open(music.linkCertificate, '_blank');
  };

  const openMusic = (event, music) => {
    event.stopPropagation();
    handleClickModalMusic(music);
  };

  const musics = (dataMusics?.findUserMusics || []).filter(music =>
    lower(music.title).includes(lower(word))
  );


  return (
    <Index>
      <Posts />
      {/* <Suspense fallback={<Loading />}> */}
      {!loadingMusics && dataMusics ? (
        <S.BackgroundSection>
          {dataMusics.findUserMusics.length === 0 ? (
            <div className="notMusic">
              <UndrawNoData />
              <span>Você não possui músicas registradas</span>
              <button type="button" onClick={() => handleRegisterMusic()}>
                Registrar música agora
              </button>
            </div>
          ) : (
            <>
              <div>
                <h1>Minhas Músicas</h1>
                <div className="inputIcon">
                  <input
                    type="text"
                    value={word}
                    placeholder="Pesquisar"
                    onChange={handleChange}
                  />
                  <BsSearch size={13} />
                </div>
              </div>
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th className="date">
                        <BsChevronDown />
                        DATA DA SOLICITAÇÃO
                      </th>
                      <th>NOME</th>
                      <th>MÚSICA</th>
                      <th>COAUTORES</th>
                      <th>INTÉRPRETES</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {musics.map(music => (
                      <tr
                        key={music.id}
                        onClick={() => handleClickModalMusic(music)}
                      >
                        <td>
                          {Moment(music.created_at).format('DD/MM/YYYY')}
                        </td>
                        <td>
                          <Highlight toHighlight={word}>
                            {music.title}
                          </Highlight>
                        </td>
                        <td className="icon">
                          <button
                            type="button"
                            className="viewMusic"
                            onClick={event => openMusic(event, music)}
                          >
                            <BsMusicNoteBeamed aria-hidden="true" />
                            Ver música
                          </button>
                        </td>
                        <td className="names">
                          {getNames(music.co_author).join(', ') || '-'}
                        </td>
                        <td className="names">
                          {getNames(music.interpreter).join(', ') || '-'}
                        </td>
                        <td id={isCertified(music.status_id) ? 'green' : 'orange'}>
                          {isCertified(music.status_id) ? (
                            <button
                              type="button"
                              onClick={event => openCertificate(event, music)}
                            >
                              <span>Baixar certificado</span>
                            </button>
                          ) : (
                            <span>{getStatusByCode(music.status_id)}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="musicList">
                {musics.map(music => {
                  const coAuthors = getNames(music.co_author);
                  const interpreters = getNames(music.interpreter);

                  return (
                    <li key={music.id}>
                      <button
                        type="button"
                        className="musicCard"
                        onClick={() => handleClickModalMusic(music)}
                      >
                        <strong className="musicCardTitle">
                          <Highlight toHighlight={word}>{music.title}</Highlight>
                        </strong>
                        <span className="musicCardMeta">
                          {Moment(music.created_at).format('DD/MM/YYYY')}
                        </span>
                        {coAuthors.length > 0 && (
                          <span className="musicCardMeta">
                            {creditLabel(coAuthors, 'Coautor', 'Coautores')}:{' '}
                            {coAuthors.join(', ')}
                          </span>
                        )}
                        {interpreters.length > 0 && (
                          <span className="musicCardMeta">
                            {creditLabel(interpreters, 'Intérprete', 'Intérpretes')}:{' '}
                            {interpreters.join(', ')}
                          </span>
                        )}
                        <span className="viewMusic">
                          <BsMusicNoteBeamed aria-hidden="true" />
                          Ver música
                        </span>
                        {!isCertified(music.status_id) && (
                          <span className="musicCardStatus">
                            {getStatusByCode(music.status_id)}
                          </span>
                        )}
                      </button>
                      {isCertified(music.status_id) && (
                        <button
                          type="button"
                          className="musicCardCertificate"
                          onClick={event => openCertificate(event, music)}
                        >
                          <BsDownload aria-hidden="true" />
                          Baixar certificado
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {selectedMusic && (
            <Modal
              isOpen={!!selectedMusic}
              onClickClose={() => setSelectedMusic(null)}
            >
              <div className="myMusics">
                <section className="musicLink">
                  <label htmlFor="musicLinkInput">Link da música</label>
                  <input
                    id="musicLinkInput"
                    type="text"
                    readOnly
                    value={musicRegister || ''}
                    onFocus={event => event.target.select()}
                  />
                  <span className="musicLinkActions">
                    <button type="button" onClick={copyMusicLink}>
                      <BsLink aria-hidden="true" />
                      Copiar link
                    </button>
                    <button type="button" onClick={shareMusicLink}>
                      <BsWhatsapp aria-hidden="true" />
                      Compartilhar no WhatsApp
                    </button>
                  </span>
                </section>
                <ul className="credits">
                  <li className="creditsTitle">
                    <span>Título</span>
                    <strong>{selectedMusic.title}</strong>
                  </li>
                  <li>
                    <span>Autor</span>
                    <strong>{dataProfile.nickname}</strong>
                  </li>
                  <li>
                    <span>
                      {creditLabel(getNames(selectedMusic.co_author), 'Coautor', 'Coautores')}
                    </span>
                    <strong>
                      {getNames(selectedMusic.co_author).join(', ') || 'Sem coautores'}
                    </strong>
                  </li>
                  <li>
                    <span>
                      {creditLabel(getNames(selectedMusic.interpreter), 'Intérprete', 'Intérpretes')}
                    </span>
                    <strong>
                      {getNames(selectedMusic.interpreter).join(', ') || 'Sem intérpretes'}
                    </strong>
                  </li>
                </ul>
                <ul>
                  <li className="audioLabel">
                    <span>Áudio</span>
                  </li>
                  <li className="audioPlay">
                    {(extension !== 'txt' && extension) ? (
                      <AudioPlayer
                        layout="horizontal"
                        customAdditionalControls={[]}
                        showJumpControls={false}
                        customVolumeControls={[]}
                        customControlsSection={[
                          RHAP_UI.MAIN_CONTROLS,
                          <button
                            key="download"
                            type="button"
                            className="audioDownload"
                            aria-label={downloadingAudio ? 'Baixando áudio' : 'Baixar áudio'}
                            disabled={downloadingAudio}
                            onClick={downloadMusic}
                          >
                            <BsDownload aria-hidden="true" />
                          </button>,
                        ]}
                        src={MP3}
                      />
                    ) : (
                      <span>Composição sem áudio</span>
                    )}
                  </li>
                </ul>
                <ul>
                  <li>
                    {(selectedMusic.letter.length <= 1 && selectedMusic.letter[0] === "") ? (
                      <p>Composição sem letra</p>
                    ) : (
                      <>
                        <span className="lyricsLabel">Letra</span>
                        {selectedMusic.letter.map((letra) => (
                          <p key={Math.random()}>
                            {letra !== '' ? letra : <br />}
                          </p>
                        ))}
                      </>
                    )}
                  </li>
                </ul>
              </div>
            </Modal>
          )}

          {dataMusics?.findUserMusics && dataMusics.findUserMusics.length > 0 && totalEstimado > limit && (
            <Pagination
              limit={limit}
              total={totalEstimado}
              currentPages={currentPages}
              setCurrentPages={setCurrentPages}
            />
          )}

          {/* Modais */}

          {modalShow === 'showNotProfile' && (
            <Modal isOpen={modalShow} onClickClose={() => setModalShow('')}>
              <h1 className="h1ButtonRegisterMusic">
                Falta pouco para você se tornar um grande compositor de sucesso
              </h1>
              <p className="pButtonRegisterMusic">
                Complete seu cadastro para adicionar créditos
              </p>

              <Link className="buttonModal" to="/dashboard/perfil">
                Completar cadastro
              </Link>
            </Modal>
          )}

          {modalShow === 'showNotCredit' && (
            <Modal isOpen={modalShow} onClickClose={() => setModalShow('')}>
              <h1 className="h1ButtonRegisterMusic">
                Falta pouco para você se tornar um grande compositor de sucesso
              </h1>
              <p className="pButtonRegisterMusic">
                Garanta a segurança de sua obra adicionando crédito para
                registrar sua música
              </p>

              <Link
                className="buttonModal"
                to="/dashboard/adicionar-creditos/planos"
              >
                Adicionar crédito
              </Link>
            </Modal>
          )}
        </S.BackgroundSection>
      ) : <Loading />}
      {/* </Suspense> */}
    </Index>
  );
};

export default MinhasMusicas;
